import { IOption } from "../components/inputs/select-dropdown/select-dropdown";
import {AUTO_PROCESS_EXECUTE, SINCE_UNIT} from "../pages/policies/constants";
import { REQUIRE_CRMA_FOLLOWUP_QUESTIONS } from "../services/api/create-rma-service";
import {DATE_FORMAT, TIME_FORMAT} from "../services/timezone-service";
import { IPlatform } from "../stores/platform-service-store";
import { IPlan } from "./Plan";
import { IReturnReason } from "./ReturnReasonTypes";

export enum USER_ACCESS_ENUM {
    Administrator = 'Administrator',
    Merchant = 'Merchant',
    User = 'User',
}

export enum USER_PLATFORM {
    PLATFORM_SHOPIFY_STORE = 'backend/shopify',
    PLATFORM_MAGENTO2_STORE = 'backend/magento2',
    PLATFORM_SALESFORCE_STORE = 'backend/salesforce',
    PLATFORM_CUSTOM_PLATFORM_STORE = 'backend/custom',
    PLATFORM_BIGCOMMERCE_STORE = 'backend/bigcommerce',
    PLATFORM_PORTAL = 'portal',
    SHOPIFY = 'shopify',
    INVITED_DIRECT = 'invited/direct',
}

export const AVAILABLE_USER_PLATFORMS = [
    USER_PLATFORM.SHOPIFY,
    USER_PLATFORM.INVITED_DIRECT,
];

export interface IOwnerVerificationStatus {
    is_verified: boolean;
    message: string;
}

export interface IUser {
    id: number;
    name: string;
    email: string;
    display_name: string;
    owner_user: number;
    platform: USER_PLATFORM;
    has_multiple_shops: number;
    move_to_plan: number | null;
    plan_id: number | null;
    shopify_freemium: number;
    shopify_grandfathered: number;
    shopify_url: string | null;
    user_access: USER_ACCESS_ENUM;
    created_at: string;
    setting?: ISetting;
    platform_service: IPlatform;
    is_admin: boolean;
    is_admin_logged_in_as_merchant: boolean;
    is_subscriptions_blocked: boolean;
    partner_id: string | null;
    has_notify_refund_integration: boolean;
    return_go_plan?: IPlan;
    shopify_plan_display_name?: string;
    is_child: boolean;
    has_children: boolean;
    missing_scopes: string | null;
    _shop_owner_verification_status: IOwnerVerificationStatus,
    _approve_scopes_redirect_url: string | null;
    _has_missing_translations: boolean;
}

export interface IShopTag {
    tag: string;
    isSelectable: boolean;
}

export interface IStoreMedia {
    base64_content: string;
    type: string;
}

export interface ISetting {
    currencies: ICurrency[] | [];
    refund_settings: IRefundSetting[] | [];
    returngo_fees_settings: IReturngoFeesSetting[] | [];
    return_paid_by: string;
    store_cost_margin: string;
    returned_item_value: number;
    cost_of_handling_a_return: number;
    gift_cards_expire_type: string;
    store_name: string;
    store_url: string;
    admin_path: string;
    return_policy_url: string;
    customer_support: string;
    gift_cards_expire: string;
    return_credits_expire: number;
    return_credits_expire_type: string;
    calculate_price_diff_by: PRICE_CALCULATIONS;
    calculate_product_exchange_price_diff_by: PRICE_CALCULATIONS;
    product_exchange_changes_to_new_item_price: PRICE_CHANGE_SETTING;
    variant_exchange_changes_to_new_item_price: PRICE_CHANGE_SETTING;
    return_label_fee_payment: RETURN_LABEL_FEE_PAYMENT;
    default_refund_level: REFUND_LEVELS;
    order_delivery_fallback: ORDER_DELIVERY_FALLBACK;
    refunds_priority: REFUNDS_PRIORITY;
    out_of_policy_item: OUT_OF_POLICY;
    validation_restock_trigger: VALIDATION_RESTOCK_TRIGGER;
    notify_refund_when: NOTIFY_REFUND_WHEN;
    support_multiple_currencies: boolean;
    is_rounding: boolean;
    allow_multiple_return_requests: boolean;
    allow_unfulfilled: boolean;
    show_disabled_resolution: boolean;
    disable_order_notes: boolean;
    allow_refund_of_exchanged: boolean;
    auto_populate_refund_amount: boolean;
    enable_gift_returns: boolean;
    is_validation_enabled: boolean;
    is_restock_note_mandatory: boolean;
    is_validate_all_enabled: boolean;
    is_bxgy_enabled: boolean;
    order_delivery_fallback_days: number;
    shipment_in_transit_fallback: number;
    return_policy_period: number;
    api_strict_types: boolean;
    restock_by: RESTOCK_BY;
    include_shipping_cost_in_refund: INCLUDE_SHIPPING_COST_IN_REFUND;
    search_product_by: ISearchField[] | [];
    line_items_refund_transactions: LINE_ITEMS_REFUND_TRANSACTIONS;
    bxgy_breakable: boolean;
    bxgy_compensate_by: BXGY_COMPENSATE_BY;
    include_shipping_cost_in_refund_for_all_items_only: boolean;
    request_customers_confirm_policy: boolean;
    strict_delivery_date: boolean;
    is_browse_by_collection_active: boolean;
    merchant_collections: IMerchantCollection[] | [];
    collection_titles?: string;
    instant_exchange_setting: IInstantExchangeSetting;
    switch_to_exchange_setting: ISwitchToExchangeSetting;
    cap_discount_setting: ICapDiscountSetting;
    _returns_portal_link: string;
    _returns_portal_preview_link: string;
    _tracking_portal_preview_link: string;
    exchange_balancing: EXCHANGE_BALANCING;
    exchange_discount_type: EXCHANGE_DISCOUNT_TYPE;
    capture_payment_trigger: AUTO_PROCESS_TRIGGER;
    require_instant_exchange: boolean;
    capture_payment_validated_qualifier: AUTO_PROCESS_VALIDATED_QUALIFIER;
    enable_portal_checkout: boolean;
    minimum_payment_threshold: number;
    use_pre_authorize: boolean;
    refund_to_bank_setting: IRefundToBankSetting;
    portal_isolation_mode: ISOLATION_MODE;
    _is_merchant_onboarded: boolean;
    create_rma_settings: ICreateRmaDefaultSettings;
    rma_level_bonus_limit_setting: IRmaLevelBonusLimitSetting;
    require_manager_approval: boolean;
    notification_only_mode_setting: INotificationOnlyModeSetting;
    auto_select_same_policy: boolean;
    timezone: string;
    date_format: DATE_FORMAT;
    time_format: TIME_FORMAT;
    cancel_inactive_rmas_settings?: ICancelInactiveRmasSettings[]; /** field marked as optional for BC for orthofeet */
    cancel_inactive_rmas: boolean;
    any_to_any_exchanges_setting: IAnyToAnyExchangesSetting;
    gift_card_refund_limit_percentage: number;
    store_credits_refund_limit_percentage: number;
    fulfill_exchanges_based_on_order_country: boolean;
    is_cancel_shipment_label_enabled: boolean;
    show_ship_to_address: boolean;
    enable_portal_v2: boolean;
    label_fee_rounding_condition: LABEL_FEE_ROUNDING,
    label_fee_rounding_amount: number|null;

    store_favicon: string | null;
    _store_new_favicon: IStoreMedia | null;
    _store_delete_favicon: boolean;

    store_page_title: string;

    /** Exchange locations filtering feature */
    is_filtering_exchange_locations_enabled: boolean;
    merchant_filtering_exchange_locations: IMerchantFilteringExchangeLocation[];

    /** Address & Packing Slips */
    enabled_discreet_sender: boolean;

    /** App\Models\Setting — optional on partial payloads (e.g. search-stores). */
    use_custom_return_portal_url?: boolean;
    /** App\Models\Setting — optional on partial payloads (e.g. search-stores). */
    custom_return_portal_url?: string | null;
}

/**
 * Store row from GET /rms-api/search-stores. Custom portal URL fields are the same as on {@link ISetting}.
 */
export interface IShop extends Pick<ISetting, 'use_custom_return_portal_url' | 'custom_return_portal_url'> {
    id: number;
    name: string;
    move_to_plan?: number;
    plan_id?: number;
    isChild?: boolean;
    parentName?: string;
    global_e_user_id?: string | null;
    global_e_env?: string | null;
}

export interface ICancelInactiveRmasSettings {
    id: number;
    setting_id: number;
    customer_action: CUSTOMER_ACTIONS;
    cancel_after_days: number|null;
    reason: string;
    _has_changes?: boolean;
}

export enum CUSTOMER_ACTIONS {
    CUSTOMER_ACTION_PAY_CHARGED_FEES_INVOICE = 'pay_charged_fee_invoice',
    CUSTOMER_ACTION_PAY_EXCHANGE_ORDER_INVOICE = 'pay_exchange_order_invoice',
    CUSTOMER_ACTION_SHIPMENT_IN_TRANSIT = 'shipment_in_transit',
    CUSTOMER_ACTION_SHIPMENT_DELIVERED_DOMESTIC = 'shipment_delivered_domestic',
    CUSTOMER_ACTION_SHIPMENT_DELIVERED_INTERNATIONAL = 'shipment_delivered_international',
}

export interface ICustomizationClassic {
    logo?: string;
    font?: string;
    text_color?: string;
    title_color?: string;
    error_color?: string;
    input_text_color?: string;
    input_background_color?: string;
    button_background_color?: string;
    button_text_color?: string;
    sku_display: boolean;
    min_embedded_offset_top: number;
    display_vertical: boolean;
    variant_selection: VARIANTS_VIEW;
    action_selection: ACTIONS_VIEW;
    show_actions_descriptions: boolean;
    order_tracking_show_eta: boolean;
    tracking_error_message_style: TRACKING_ERROR_MESSAGE_STYLE;
    portal_css: string;
    portal_position: PORTAL_POSITION;
    top_margin: number;
    bottom_margin: number;
    container_margins: number;
    portal_border_radius: number;
    button_border_radius: number;
    elements_border_radius: number;
    return_portal_background_type: PORTAL_BACKGROUND_TYPE;
    return_portal_background_value: string;
    return_portal_background_size: PORTAL_BACKGROUND_SIZE;
    return_portal_foreground_color: string;
    return_portal_foreground_opacity: number;
    tracking_portal_background_type: PORTAL_BACKGROUND_TYPE;
    tracking_portal_background_value: string;
    tracking_portal_background_size: PORTAL_BACKGROUND_SIZE;
    tracking_portal_foreground_color: string;
    tracking_portal_foreground_opacity: number;
    return_portal_cover_image: string;
    policy_rule_display: POLICY_RULE_DISPLAY;
    footer_background_color: string;
    footer_background_opacity: number;
    show_new_items_first: boolean;
    show_reduced_price_percentage: boolean;
    s2e_layout_type: S2E_LAYOUT_TYPE;
    s2e_layout_data: Array<IS2ELayoutItem>;
}

export interface IEdgeCustomization {
    logo?: string;
    font: string;
    text_color: string;
    title_color: string;
    error_color: string;
    input_text_color: string;
    input_background_color: string;
    button_background_color: string;
    button_text_color: string;

    portal_css?: string;
    portal_border_radius: number;
    button_border_radius: number;
    elements_border_radius: number;
    return_portal_background_type: PORTAL_BACKGROUND_TYPE;
    return_portal_background_value: string;
    return_portal_background_size: PORTAL_BACKGROUND_SIZE;
    return_portal_foreground_color: string;
    return_portal_foreground_opacity: number;
    return_portal_cover_image?: string;
    footer_background_color: string;
    s2e_layout_type: S2E_LAYOUT_TYPE;
    s2e_layout_data?: Array<IS2ELayoutItem>;

    header_background_color: string;
    success_color: string;
    portal_secondary_background_color: string;
    portal_secondary_background_opacity: number;
    stroke_color: string;
    popup_overlay_color: string;
    popup_overlay_opacity: number;
    drop_shadow_color: string;
    drop_shadow_opacity: number;
}

export interface IS2ELayoutItem {
    image?: string;
    link?: string;
}

export enum TRACKING_ERROR_MESSAGE_STYLE {
    INFO = "INFO",
    ERROR = 'ERROR',
    WARNING = 'WARNING',
}

export enum PORTAL_POSITION {
    LEFT = 'LEFT',
    CENTER = 'CENTER',
    RIGHT = 'RIGHT'
}

export enum PORTAL_BACKGROUND_TYPE {
    IMAGE = 'IMAGE',
    COLOR = 'COLOR'
}

export enum PORTAL_BACKGROUND_SIZE {
    STRETCH = 'STRETCH',
    FIT = 'FIT',
    REPEAT = 'REPEAT'
}

export enum POLICY_RULE_DISPLAY {
    RESOLUTION = 'RESOLUTION',
    RETURN_METHOD = 'RETURN-METHOD'
}

export const POLICY_RULE_DISPLAY_MAP = {
    [POLICY_RULE_DISPLAY.RESOLUTION]: { value: POLICY_RULE_DISPLAY.RESOLUTION, label: 'Resolution is primary, return method is secondary' },
    [POLICY_RULE_DISPLAY.RETURN_METHOD]: { value: POLICY_RULE_DISPLAY.RETURN_METHOD, label: 'Return method is primary, resolution is secondary' },
}

export function policyRuleDisplayList() {
    return Object.values(POLICY_RULE_DISPLAY_MAP);
}

export const BORDER_RADIUS_LIMITS_MAP = {
    portal_border_radius: { min: 0, max: 18 },
    button_border_radius: { min: 0, max: 18 },
    elements_border_radius: { min: 0, max: 36 }
}

export enum S2E_LAYOUT_TYPE {
    ONE_RECTANGLE = 'ONE-RECTANGLE',
    TWO_RECTANGLES = 'TWO-RECTANGLES',
    THREE_RECTANGLES = 'THREE-RECTANGLES',
    TWO_SQUARES = 'TWO-SQUARES',
    FOUR_SQUARES = 'FOUR-SQUARES'
}

export const S2E_LAYOUT_TYPE_MAP = {
    [S2E_LAYOUT_TYPE.ONE_RECTANGLE]: { value: S2E_LAYOUT_TYPE.ONE_RECTANGLE, numberOfImages: 1, imageAspectRatio: '2:1' },
    [S2E_LAYOUT_TYPE.TWO_RECTANGLES]: { value: S2E_LAYOUT_TYPE.TWO_RECTANGLES, numberOfImages: 2, imageAspectRatio: '3:1' },
    [S2E_LAYOUT_TYPE.THREE_RECTANGLES]: { value: S2E_LAYOUT_TYPE.THREE_RECTANGLES, numberOfImages: 3, imageAspectRatio: '4:5' },
    [S2E_LAYOUT_TYPE.TWO_SQUARES]: { value: S2E_LAYOUT_TYPE.TWO_SQUARES, numberOfImages: 2, imageAspectRatio: '1:1' },
    [S2E_LAYOUT_TYPE.FOUR_SQUARES]: { value: S2E_LAYOUT_TYPE.FOUR_SQUARES, numberOfImages: 4, imageAspectRatio: '1:1' },
}

export interface IReturnScoreSetting {
    return_paid_by: string;
    store_cost_margin: string;
    returned_item_value: number;
    cost_of_handling_a_return: number;
}

export interface IMerchantCollection {
    id?: string;
    title: string;
}

export interface IBrowseByCollection {
    is_browse_by_collection_active: boolean;
    collection_titles: string;
}

export interface IInstantExchangeSettingAdditionalInfo {
    has_payment_service_integrations: boolean;
    has_variant_exchange_resolution_named_instant_exchange: boolean;
}
export interface IInstantExchangeSetting {
    id: number;
    is_active: boolean;
    _additional_info: IInstantExchangeSettingAdditionalInfo;
    auth_period_in_days: number;
}

export interface ISwitchToExchangeSettingAdditionalInfo {
    eligible_return_reasons: IReturnReason[];
}

export interface ISwitchToExchangeSetting {
    id: number;
    is_active: boolean;
    incentive_type: INCENTIVE_TYPES;
    incentive_amount: number;
    incentive_currency?: string;
    incentive_virtual_currency: INCENTIVE_VIRTUAL_CURRENCIES;
    _additional_info: ISwitchToExchangeSettingAdditionalInfo;
}

export interface ICapDiscountSetting {
    id: number;
    product_exchange_price_diff_calc_by: CAP_PRICE_DIFF_CALC_BY;
    variant_exchange_price_diff_calc_by: CAP_PRICE_DIFF_CALC_BY;
}

export interface IRefundToBankSetting {
    id: number;
    show_first_name: boolean,
    require_first_name: boolean,
    show_last_name: boolean,
    require_last_name: boolean,
    show_branch_name: boolean,
    require_branch_name: boolean,
    show_branch_address: boolean,
    require_branch_address: boolean,
    show_comment: boolean,
    require_comment: boolean,
}

export interface ICurrency {
    currency_code: string;
}

export interface IRefundSetting {
    refund_type: number;
    setting_field: string;
    is_automatic_enabled: boolean;
    is_manual_enabled: boolean;
}

export interface IStoreRole {
    id: number;
    name: string;
    display_name: string;
    permissions?: IStoreRolePermission[]
}

interface IStoreRolePermission {
    permission_id: number;
    role_id: number;
}

export interface ISearchField {
    search_by_field: SEARCH_PRODUCT_BY_FIELDS;
}

export interface IReturngoFeesSetting {
    fee_type: string;
    add_tax: boolean;
}

/** This is the CreateRmaSetting model on server */
export interface ICreateRmaDefaultSettings {
    resolution_id: number|null;
    return_method_id: number|null;
    merchant_return_reason_id: number|null;
    restocking_fee: number|null;
    adjustments: number|null;
    is_auto_refund: boolean,
    auto_refund_trigger: AUTO_PROCESS_TRIGGER | null,
    auto_refund_validated_qualifier?: AUTO_PROCESS_VALIDATED_QUALIFIER | null,
    auto_refund_execute: AUTO_PROCESS_EXECUTE | null,
    auto_refund_since_value?: number | null,
    auto_refund_since_unit?: SINCE_UNIT | null,
    auto_mark_done: boolean,
    available_return_reasons: C_RMA_AVAILABLE_REASONS,
    send_customer_emails: boolean,
    require_crma_followup_questions: REQUIRE_CRMA_FOLLOWUP_QUESTIONS,
    _has_changes?: boolean
}

export interface IRmaLevelBonusLimitSetting {
    id: number;
    setting_id: number;
    is_active: boolean;
    refund_bonuses_limit: number | null;
    _has_changes?: boolean;
}

export interface INotificationOnlyModeSetting {
    id: number;
    setting_id: number;
    is_active: boolean;
    refund_to_payment_method: boolean;
    refund_to_gift_card: boolean;
    refund_to_store_credit: boolean;
    _has_changes?: boolean;
}

export interface IAnyToAnyExchangesSetting {
    id: number;
    enable_shop_now: boolean;
}

export enum VALIDATION_RESTOCK_TRIGGER {
    AUTOMATIC = 'automatic',
    MANUALLY = 'manually',
}

export const VALIDATION_RESTOCK_TRIGGER_ARR = [
    { value: VALIDATION_RESTOCK_TRIGGER.AUTOMATIC, label: 'VALIDATION_RESTOCK_TRIGGER_AUTOMATIC' },
    { value: VALIDATION_RESTOCK_TRIGGER.MANUALLY, label: 'VALIDATION_RESTOCK_TRIGGER_MANUALLY' },
]

export enum NOTIFY_REFUND_WHEN {
    ITEMS_VALIDATED = 'items_validated',
    RMA_REFUNDED = 'rma_refunded',
    ITEMS_VALIDATED_AND_RMA_REFUNDED = 'items_validated_and_rma_refunded',
}

export const NOTIFY_REFUND_WHEN_ARR = [
    { value: NOTIFY_REFUND_WHEN.ITEMS_VALIDATED, label: 'ITEMS_VALIDATED' },
    { value: NOTIFY_REFUND_WHEN.RMA_REFUNDED, label: 'RMA_REFUNDED' },
    { value: NOTIFY_REFUND_WHEN.ITEMS_VALIDATED_AND_RMA_REFUNDED, label: 'ITEMS_VALIDATED_AND_RMA_REFUNDED' },
]

export enum RETURN_LABEL_FEE_PAYMENT {
    ALWAYS_CHARGE = 'ALWAYS_CHARGE',
    DEDUCT_FROM_REFUNDS = 'DEDUCT_FROM_REFUNDS',
    SET_PER_POLICY_RULE = 'SET_PER_POLICY_RULE',
}

export enum LABEL_FEE_ROUNDING {
    LABEL_FEE_ROUNDING_UP = 'rounding_up',
    LABEL_FEE_ROUNDING_DOWN = 'rounding_down',
    LABEL_FEE_ROUNDING_NEAREST = 'rounding_nearest',
    LABEL_FEE_NO_ROUNDING = 'no_rounding'
}

export const RETURN_LABEL_FEE_PAYMENT_MAP = {
    [RETURN_LABEL_FEE_PAYMENT.ALWAYS_CHARGE]: { label: 'ALWAYS_APPLY_SEPARATE_CHARGE_FOR_RETURN_LABEL_FEE' },
    [RETURN_LABEL_FEE_PAYMENT.DEDUCT_FROM_REFUNDS]: { label: 'DEDUCT_LABEL_FEE_FROM_REFUNDS' },
    [RETURN_LABEL_FEE_PAYMENT.SET_PER_POLICY_RULE]: { label: 'SET_RETURN_LABEL_FEE_BASED_ON_POLICY_RULES' },
}

export enum REFUND_LEVELS {
    LINE_ITEM = 'LineItem',
    ORDER = 'Order',
}

export const REFUND_LEVELS_MAP = {
    [REFUND_LEVELS.LINE_ITEM]: { label: 'LINE_ITEM' },
    [REFUND_LEVELS.ORDER]: { label: 'ORDER' },
}

export enum ORDER_DELIVERY_FALLBACK {
    DEFAULT = 'default',
    DAYS_SINCE_FULFILLMENT = 'days_since_fulfillment',
}

export const ORDER_DELIVERY_FALLBACK_MAP = {
    [ORDER_DELIVERY_FALLBACK.DEFAULT]: { label: 'UPDATED_BY_TRACKING' },
    [ORDER_DELIVERY_FALLBACK.DAYS_SINCE_FULFILLMENT]: { label: 'DAYS_PASSED_SINCE_FULFILLMENT' },
}

export enum EXCHANGE_BALANCING {
    DISCOUNT_APPLIED = 'DISCOUNT_APPLIED',
    GIFT_CARD_PAYMENT = 'GIFT_CARD_PAYMENT',
}

export const EXCHANGE_BALANCING_MAP = {
    [EXCHANGE_BALANCING.DISCOUNT_APPLIED]: { label: 'DISCOUNT_APPLIED' },
    [EXCHANGE_BALANCING.GIFT_CARD_PAYMENT]: { label: 'GIFT_CARD_PAYMENT' },
}

export enum EXCHANGE_DISCOUNT_TYPE {
    LINE_ITEM_DISCOUNT = 'LINE_ITEM_DISCOUNT',
    ORDER_LEVEL_DISCOUNT = 'ORDER_LEVEL_DISCOUNT',
}

export const EXCHANGE_DISCOUNT_TYPE_MAP = {
    [EXCHANGE_DISCOUNT_TYPE.LINE_ITEM_DISCOUNT]: { label: 'LINE_ITEM_DISCOUNT' },
    [EXCHANGE_DISCOUNT_TYPE.ORDER_LEVEL_DISCOUNT]: { label: 'ORDER_LEVEL_DISCOUNT' },
}

export enum PRICE_CALCULATIONS {
    PAID_PRICE = 'paid_price',
    CATALOG_PRICE = 'catalog_price',
    CATALOG_PRICE_TIME_OF_PURCHASE = 'catalog_price_time_of_purchase',
}

export const PRICE_CALCULATIONS_MAP = {
    [PRICE_CALCULATIONS.PAID_PRICE]: { label: 'PAID_PRICE' },
    [PRICE_CALCULATIONS.CATALOG_PRICE]: { label: 'CATALOG_PRICE' },
    [PRICE_CALCULATIONS.CATALOG_PRICE_TIME_OF_PURCHASE]: { label: 'CATALOG_PRICE_TIME_OF_PURCHASE' },
}

export enum PRICE_CHANGE_SETTING {
    OVERRIDE_THE_ORIGINAL_REQUEST = 'OVERRIDE_THE_ORIGINAL_REQUEST',
    FOLLOW_THE_ORIGINAL_REQUEST = 'FOLLOW_THE_ORIGINAL_REQUEST',
}
export const PRICE_CHANGE_MAP = {
    [PRICE_CHANGE_SETTING.OVERRIDE_THE_ORIGINAL_REQUEST]: { label: 'OVERRIDE_THE_ORIGINAL_REQUEST', description: 'OVERRIDE_THE_ORIGINAL_REQUEST_DESCRIPTION' },
    [PRICE_CHANGE_SETTING.FOLLOW_THE_ORIGINAL_REQUEST]: { label: 'FOLLOW_THE_ORIGINAL_REQUEST', description: 'FOLLOW_THE_ORIGINAL_REQUEST_DESCRIPTION' },
}


export enum INCENTIVE_TYPES {
    FIXED_PRICE = 'FIXED_PRICE',
    PERCENTAGE = 'PERCENTAGE',
}

export const INCENTIVE_TYPES_MAP = {
    [INCENTIVE_TYPES.FIXED_PRICE]: { label: 'FIXED_PRICE' },
    [INCENTIVE_TYPES.PERCENTAGE]: { label: 'PERCENTAGE' },
}

export const A2A_INCENTIVE_TYPES_MAP = {
    [INCENTIVE_TYPES.FIXED_PRICE]: { label: 'FIXED_PRICE' },
    [INCENTIVE_TYPES.PERCENTAGE]: { label: 'PERCENTAGE_OF_ITEMS_VALUE' },
}

export enum INCENTIVE_VIRTUAL_CURRENCIES {
    STORE_CREDITS = 'STORE_CREDITS',
    GIFT_CARD = 'GIFT_CARD',
}

export const INCENTIVE_VIRTUAL_CURRENCIES_MAP = {
    [INCENTIVE_VIRTUAL_CURRENCIES.STORE_CREDITS]: { label: 'STORE_CREDITS' },
    [INCENTIVE_VIRTUAL_CURRENCIES.GIFT_CARD]: { label: 'GIFT_CARD' },
}

export enum REFUNDS_PRIORITY {
    MONEY = 'REFUNDS_PRIORITY_MONEY',
    VIRTUAL_CURRENCY = 'REFUNDS_PRIORITY_VIRTUAL_CURRENCY',
}

export const REFUNDS_PRIORITY_MAP = {
    [REFUNDS_PRIORITY.MONEY]: { label: 'MONEY_VIRTUAL_CURRENCY' },
    [REFUNDS_PRIORITY.VIRTUAL_CURRENCY]: { label: 'VIRTUAL_CURRENCY_MONEY' },
}

export enum OUT_OF_POLICY {
    ITEM_NOT_CREATED = 'NOT_CREATED',
    ITEM_REFERENCED = 'REFERENCED',
    ITEM_INCLUDED = 'INCLUDED',
}

export const OUT_OF_POLICY_MAP = {
    [OUT_OF_POLICY.ITEM_NOT_CREATED]: { label: 'RMA_WITH_OUT_OF_POLICY_WILL_NOT_BE_CREATED' },
    [OUT_OF_POLICY.ITEM_REFERENCED]: { label: 'OUT_OF_POLICY_ITEMS_WILL_BE_REFERENCED_IN_RMA' },
    [OUT_OF_POLICY.ITEM_INCLUDED]: { label: 'OUT_OF_POLICY_ITEMS_WILL_BE_INCLUDED_IN_RMA' },
}

export enum RESTOCK_BY {
    RESTOCK_BY_REFUND_PROCESS = 'RESTOCK_BY_REFUND_PROCESS',
    RESTOCK_BY_INCREASING_INVENTORY = 'RESTOCK_BY_INCREASING_INVENTORY',
}

export const RESTOCK_BY_MAP = {
    [RESTOCK_BY.RESTOCK_BY_REFUND_PROCESS]: { label: 'REFUND_ZERO_AND_RESTOCK' },
    [RESTOCK_BY.RESTOCK_BY_INCREASING_INVENTORY]: { label: 'PLUS_ONE_TO_INVENTORY_ONLY' },
}

export enum INCLUDE_SHIPPING_COST_IN_REFUND {
    YES = 'YES',
    NO = 'NO',
    SET_PER_POLICY_RULE = 'SET_PER_POLICY_RULE',
    CUSTOM = 'CUSTOM',
}

export const INCLUDE_SHIPPING_COST_IN_REFUND_MAP = {
    [INCLUDE_SHIPPING_COST_IN_REFUND.YES]: { label: 'YES' },
    [INCLUDE_SHIPPING_COST_IN_REFUND.NO]: { label: 'NO' },
    [INCLUDE_SHIPPING_COST_IN_REFUND.SET_PER_POLICY_RULE]: { label: 'PER_POLICY_RULE' },
    [INCLUDE_SHIPPING_COST_IN_REFUND.CUSTOM]: { label: 'CUSTOM' },
}

export enum REFUND_TYPES {
    PAYMENT_METHOD = 0,
    STORE_CREDITS = 1,
    BANK_REFUND = 2,
    GIFT_CARD = 3,
}

export const REFUND_TYPES_MAP = {
    [REFUND_TYPES.PAYMENT_METHOD]: { label: 'Payment Method' },
    [REFUND_TYPES.STORE_CREDITS]: { label: 'Store Credits' },
    [REFUND_TYPES.BANK_REFUND]: { label: 'Bank Refund' },
    [REFUND_TYPES.GIFT_CARD]: { label: 'New Gift Card' },
}

export const SHIPPING_COST_REFUND_SETTING = 'SHIPPING_COST_REFUND';

export enum EXCHANGE_TYPES {
    VARIANT_EXCHANGE = 'variant exchange',
    PRODUCT_EXCHANGE = 'product exchange',
}

export const EXCHANGE_TYPES_MAP = {
    [EXCHANGE_TYPES.VARIANT_EXCHANGE]: {
        value: EXCHANGE_TYPES.VARIANT_EXCHANGE, label: 'Variant Exchange'
    },
    [EXCHANGE_TYPES.PRODUCT_EXCHANGE]: {
        value: EXCHANGE_TYPES.PRODUCT_EXCHANGE, label: 'Product Exchange'
    },
}

export function exchangeTypesViewList() {
    return Object.values(EXCHANGE_TYPES_MAP);
}

// Also maintains the order of fields to be displayed.
export enum SEARCH_PRODUCT_BY_FIELDS {
    TITLE = 'title',
    DESCRIPTION = 'description',
    TYPE = 'type',
    TAGS = 'tags',
    VENDOR = 'vendor',
    SKU = 'sku',
}

export const SEARCH_PRODUCT_BY_ARR = [
    { label: 'SEARCH_BY_TITLE', key: SEARCH_PRODUCT_BY_FIELDS.TITLE },
    { label: 'SEARCH_BY_DESC', key: SEARCH_PRODUCT_BY_FIELDS.DESCRIPTION },
    { label: 'SEARCH_BY_TYPE', key: SEARCH_PRODUCT_BY_FIELDS.TYPE },
    { label: 'SEARCH_BY_TAGS', key: SEARCH_PRODUCT_BY_FIELDS.TAGS },
    { label: 'SEARCH_BY_VENDOR', key: SEARCH_PRODUCT_BY_FIELDS.VENDOR },
    { label: 'SEARCH_BY_SKU', key: SEARCH_PRODUCT_BY_FIELDS.SKU },
]

export enum LINE_ITEMS_REFUND_TRANSACTIONS {
    ONE_TRANSACTION_PER_LINE_ITEM = 'ONE_TRANSACTION_PER_LINE_ITEM',
    ONLY_ONE_TRANSACTION = 'ONLY_ONE_TRANSACTION',
}

export const LINE_ITEMS_REFUND_TRANSACTIONS_MAP = {
    [LINE_ITEMS_REFUND_TRANSACTIONS.ONE_TRANSACTION_PER_LINE_ITEM]: { label: 'ONE_TRANSACTION_PER_LINE_ITEM' },
    [LINE_ITEMS_REFUND_TRANSACTIONS.ONLY_ONE_TRANSACTION]: { label: 'ONLY_ONE_TRANSACTION' },
}
export enum BXGY_COMPENSATE_BY {
    DEDUCT_Y_FROM_X = 'DEDUCT_Y_FROM_X',
    PROPORTIONAL_VALUE = 'PROPORTIONAL_VALUE'
}

export const BXGY_COMPENSATE_BY_MAP = {
    [BXGY_COMPENSATE_BY.DEDUCT_Y_FROM_X]: { label: 'Deduct Y From X' },
    [BXGY_COMPENSATE_BY.PROPORTIONAL_VALUE]: { label: 'Proportional Value' },
}

export enum CAP_PRICE_DIFF_CALC_BY {
    COMPARE_AT_PRICE = 'COMPARE_AT_PRICE',
    CATALOG_PRICE = 'CATALOG_PRICE'
}

export const CAP_PRICE_DIFF_CALC_BY_MAP = {
    [CAP_PRICE_DIFF_CALC_BY.COMPARE_AT_PRICE]: { label: 'CAP_PRICE_DIFF_CALC_BY_COMPARE_AT_PRICE' },
    [CAP_PRICE_DIFF_CALC_BY.CATALOG_PRICE]: { label: 'CAP_PRICE_DIFF_CALC_BY_CATALOG_PRICE' },
}

export enum VARIANTS_VIEW {
    VARIANTS_VIEW_BOTH_ITEMS = 'VARIANTS_VIEW_BOTH_ITEMS',
    VARIANTS_VIEW_BOTH_OPTIONS = 'VARIANTS_VIEW_BOTH_OPTIONS',
    VARIANTS_VIEW_ITEMS = 'VARIANTS_VIEW_ITEMS',
    VARIANTS_VIEW_OPTIONS = 'VARIANTS_VIEW_OPTIONS',
}

export enum ACTIONS_VIEW {
    ACTIONS_ON_ITEM_SELECTION = 'ACTIONS_ON_ITEM_SELECTION',
    ACTIONS_AFTER_SELECTING_ITEMS = 'ACTIONS_AFTER_SELECTING_ITEMS',
}

export enum TRACKING_DEFAULT_SEARCH_INPUT {
    TRACKING_NUMBER = 'TRACKING_NUMBER',
    ORDER_NUMBER_AND_EMAIL = 'ORDER_NUMBER_AND_EMAIL',
    BOTH = 'BOTH',
}

export const VARIANTS_VIEW_MAP = {
    [VARIANTS_VIEW.VARIANTS_VIEW_BOTH_ITEMS]: {
        value: VARIANTS_VIEW.VARIANTS_VIEW_BOTH_ITEMS, label: 'Both options are offered, View as Items is the default'
    },
    [VARIANTS_VIEW.VARIANTS_VIEW_BOTH_OPTIONS]: {
        value: VARIANTS_VIEW.VARIANTS_VIEW_BOTH_OPTIONS, label: 'Both options are offered, View as Options is the default'
    },
    [VARIANTS_VIEW.VARIANTS_VIEW_ITEMS]: {
        value: VARIANTS_VIEW.VARIANTS_VIEW_ITEMS, label: 'Only View as Items is displayed'
    },
    [VARIANTS_VIEW.VARIANTS_VIEW_OPTIONS]: {
        value: VARIANTS_VIEW.VARIANTS_VIEW_OPTIONS, label: 'Only View as Options is displayed'
    },
}

export function variantsViewList() {
    return Object.values(VARIANTS_VIEW_MAP);
}

export const ACTIONS_VIEW_MAP = {
    [ACTIONS_VIEW.ACTIONS_ON_ITEM_SELECTION]: {
        value: ACTIONS_VIEW.ACTIONS_ON_ITEM_SELECTION, label: 'On item selection'
    },
    [ACTIONS_VIEW.ACTIONS_AFTER_SELECTING_ITEMS]: {
        value: ACTIONS_VIEW.ACTIONS_AFTER_SELECTING_ITEMS, label: 'After selecting items'
    },
}

export function actionsViewList() {
    return Object.values(ACTIONS_VIEW_MAP);
}

export const TRACKING_DEFAULT_SEARCH_INPUT_MAP = {
    [TRACKING_DEFAULT_SEARCH_INPUT.ORDER_NUMBER_AND_EMAIL]: {
        value: TRACKING_DEFAULT_SEARCH_INPUT.ORDER_NUMBER_AND_EMAIL, label: 'Order number + email/ZIP/phone'
    },
}

export function trackingDefaultInputList() {
    return Object.values(TRACKING_DEFAULT_SEARCH_INPUT_MAP);
}

export const OPTIONS_LAYOUT_MAP = {
    'true': { value: 'true', label: 'Vertical' },
    'false': { value: 'false', label: 'Horizontal' },
}

export function optionLayoutList() {
    return Object.values(OPTIONS_LAYOUT_MAP);
}

export interface IStoreLocation {
    name: string,
    location_id: string,
}

/** Exchange locations filtering - API-layer interfaces */

/** Special case country group */
export const REST_OF_WORLD = 'REST_OF_WORLD';

/** Location filtering rule record */
export type IMerchantFilteringExchangeLocation = IMerchantFilteringExchangeLocationWithCountryGroup | IMerchantFilteringExchangeLocationWithCountryCode;

export interface IMerchantBaseFilteringExchangeLocation {
    id?: number;
    setting_id?: number;
    location_id: string;
}

export interface IMerchantFilteringExchangeLocationWithCountryGroup extends IMerchantBaseFilteringExchangeLocation {
    predefined_country_group: typeof REST_OF_WORLD;
    country_code: null;
}

export interface IMerchantFilteringExchangeLocationWithCountryCode extends IMerchantBaseFilteringExchangeLocation {
    predefined_country_group: null;
    country_code: string;
}

/** Country zone with optional provinces and cities */
export interface ICountryZone {
    label: string;
    code: string;
    provinces?: ICountryProvince[];
    cities?: ICountryCity[];
}

/** Province with optional cities */
export interface ICountryProvince {
    label: string;
    code: string;
    cities?: ICountryCity[];
}

export interface ICountryCity {
    label: string;
    code: string;
}

export enum FEE_TYPES {
    RESTOCKING_FEE = 'RESTOCKING_FEE',
    RETURN_LABEL_FEE = 'RETURN_LABEL_FEE',
    RETURN_FEE = 'RETURN_FEE',
    CANCELLATION_FEE = 'CANCELLATION_FEE',
}

export const FEE_TYPES_MAP = {
    [FEE_TYPES.RESTOCKING_FEE]: { label: 'Restocking Fee' },
    [FEE_TYPES.RETURN_LABEL_FEE]: { label: 'Return Label Fee' },
    [FEE_TYPES.RETURN_FEE]: { label: 'Return Fee' },
    [FEE_TYPES.CANCELLATION_FEE]: { label: 'Cancellation Fee' },
}

export enum AUTO_PROCESS_TRIGGER {
    USE_DEFAULT_TRIGGER = 'use-default-trigger',
    RMA_APPROVED = 'rma-approved',
    SHIPMENT_SHIPPED = 'shipment-in-transit',
    SHIPMENT_RECEIVED = 'shipment-received',
    ITEMS_VALIDATED = 'items-validated',
}

export enum AUTO_PROCESS_VALIDATED_QUALIFIER {
    EXECUTE_WHEN_ALL_RETURNED = 'all-returned',
    EXECUTE_WHEN_ALL_RESTOCKABLE = 'all-restockable',
}

export enum ISOLATION_MODE {
    NONE = 'none',
    SHADOW_DOM = 'shadow_dom',
}

export enum C_RMA_AVAILABLE_REASONS {
    AS_IN_PORTAL = 'C_RMA_AVAILABLE_REASONS_AS_IN_PORTAL',
    ALL = 'C_RMA_AVAILABLE_REASONS_ALL',
}

export enum ACCESS_SCOPES {
	WRITE_DISCOUNTS = 'write_discounts',
}

export enum ADMIN_ROLES {
    VIEWER = 'viewer',
    EDITOR = 'editor',
}


export function getCaptureTriggerTypesOptions(getDashTranslation: (key: string) => string): IOption[] {
    return [
        {
            label: getDashTranslation('TRIGGER_SHIPMENT_SHIPPED'),
            value: AUTO_PROCESS_TRIGGER.SHIPMENT_SHIPPED,
        },
        {
            label: getDashTranslation('TRIGGER_SHIPMENT_RECEIVED'),
            value: AUTO_PROCESS_TRIGGER.SHIPMENT_RECEIVED,
        },
        {
            label: getDashTranslation('TRIGGER_ITEMS_VALIDATED'),
            value: AUTO_PROCESS_TRIGGER.ITEMS_VALIDATED,
        },
    ];
}

export function getValidatedQualifierOptions(getDashTranslation: (key: string) => string): IOption[] {
    return [
        {
            label: getDashTranslation('VALIDATED_QUALIFIER_ALL_RETURNED'),
            value: AUTO_PROCESS_VALIDATED_QUALIFIER.EXECUTE_WHEN_ALL_RETURNED,
        },
        {
            label: getDashTranslation('VALIDATED_QUALIFIER_ALL_RESTOCKABLE'),
            value: AUTO_PROCESS_VALIDATED_QUALIFIER.EXECUTE_WHEN_ALL_RESTOCKABLE,
        },
    ]
}

export function getAvailableReturnReasonsOptions(getDashTranslation: (key: string) => string): IOption[] {
    return [
        {
            label: getDashTranslation('AS_OFFERED_IN_THE_PORTAL'),
            value: C_RMA_AVAILABLE_REASONS.AS_IN_PORTAL,
        },
        {
            label: getDashTranslation('ALL_RETURN_REASONS'),
            value: C_RMA_AVAILABLE_REASONS.ALL,
        },
    ]
}


